/* import __COLOCATED_TEMPLATE__ from './change-conversation-priority.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import {
  PRIORITY_LEVELS,
  PRIORITY_LEVEL_PICKER_ORDER,
  priorityLevelDisplay,
  type PriorityLevelName,
} from 'embercom/lib/inbox/priority-levels';

interface Args {
  action: $TSFixMe;
  actionEditorContext?: { isRulePage?: boolean };
  onChange?: () => void;
  onDelete?: () => void;
  isDisabled?: boolean;
  forceInvalid?: boolean;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

// Same i18n key map as the macro composer / visual builder picker. Static
// keys per level (ember skill forbids template-literal key construction).
const LEVEL_LABEL_KEYS: Record<PriorityLevelName, string> = {
  none: 'inbox.command-k.set-priority-level.none',
  low: 'inbox.command-k.set-priority-level.low',
  medium: 'inbox.command-k.set-priority-level.medium',
  high: 'inbox.command-k.set-priority-level.high',
  urgent: 'inbox.command-k.set-priority-level.urgent',
};

// Rule-action display for "Change priority" — used by:
//   • Inbox rules page (`actionEditorContext.isRulePage === true`)
//   • Settings → Macros authoring UI (the macro action editor)
//   • Workflow visual builder's apply-rules step (Rules::Actions::Conversation)
//
// Flag OFF: renders the legacy binary "Mark as priority" label, unchanged.
// Flag ON: renders an IcSelect with all five levels. Selection writes both
// `priority` and `priority_level` to actionData — see `rule-action-service.js`
// for the dual-write rationale.
export default class RulesActionsChangeConversationPriority extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  // This component is hosted under Settings → Inbox Rules, Settings → Macros,
  // and the Workflows visual-builder apply-rules step — all non-Inbox UI.
  // The Ember skill mandates `appService` (not `session`) outside Inbox routes,
  // and `session.workspace` would be undefined when the component renders in
  // Settings → Macros authoring without an active Inbox context (see Issue 12
  // discoveries doc: "Macro Composer Spans Inbox AND Settings").
  get isMultiLevelEnabled() {
    return this.appService.app.canUseFeature('team-inbox-priority-levels');
  }

  get priorityLevels() {
    return PRIORITY_LEVEL_PICKER_ORDER.map((value) => {
      let display = priorityLevelDisplay(value);
      return {
        value,
        text: this.intl.t(LEVEL_LABEL_KEYS[display.name]),
      };
    });
  }

  get selectedPriority(): number {
    let data = this.args.action?.actionData ?? {};
    if (typeof data.priority_level === 'number') {
      return data.priority_level;
    }
    if (typeof data.priority === 'number') {
      return data.priority;
    }
    return PRIORITY_LEVELS.PRIORITY;
  }

  get label() {
    let display = priorityLevelDisplay(this.selectedPriority);
    return this.intl.t(LEVEL_LABEL_KEYS[display.name]);
  }

  @action onSelectPriority(value: number) {
    this.args.action.actionData = {
      ...this.args.action.actionData,
      priority: value,
      priority_level: value,
    };
    if (this.args.onChange) {
      this.args.onChange();
    }
  }

  @action closePicker() {
    if (this.args.action) {
      this.args.action.justAdded = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Rules::Actions::ChangeConversationPriority': typeof RulesActionsChangeConversationPriority;
  }
}
